// src/features/business-functions/hooks/useAccountTabs.js
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { accountActiveTabAtom } from '../state/accountAtoms';
import { ACCOUNT_TAB_CONFIG, DEFAULT_ACCOUNT_TAB } from '../constants/accountConstants';

/**
 * Manages dynamic tab state for the Account Detail page.
 * Syncs with URL search param (?tab=xxx) for deep-linking support.
 */
export default function useAccountTabs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useRecoilState(accountActiveTabAtom);

  const validTabKeys = useMemo(
    () => new Set(ACCOUNT_TAB_CONFIG.map((t) => t.key)),
    []
  );

  // ── Read tab from URL on mount / URL change ───────────────────────
  useEffect(() => {
    const urlTab = searchParams.get('tab');

    if (urlTab && validTabKeys.has(urlTab)) {
      setActiveTab(urlTab);
      return;
    }

    if (urlTab && !validTabKeys.has(urlTab)) {
      setActiveTab(DEFAULT_ACCOUNT_TAB);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', DEFAULT_ACCOUNT_TAB);
          return next;
        },
        { replace: true }
      );
      return;
    }

    setActiveTab(DEFAULT_ACCOUNT_TAB);
  }, [searchParams, validTabKeys, setActiveTab, setSearchParams]);

  // ── Tab change handler — shallow URL update ───────────────────────
  const handleTabChange = useCallback(
    (_event, newValue) => {
      if (!validTabKeys.has(newValue)) return;

      setActiveTab(newValue);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', newValue);
          return next;
        },
        { replace: true }
      );
    },
    [validTabKeys, setActiveTab, setSearchParams]
  );

  return {
    activeTab: validTabKeys.has(activeTab) ? activeTab : DEFAULT_ACCOUNT_TAB,
    tabConfig: ACCOUNT_TAB_CONFIG,
    handleTabChange,
  };
}
